/*
  UserReport.js - Index Component for User Reports
*/

// NPM
import React, { useState, useEffect, useCallback } from 'react';

// MUI COMPONENTS
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';

// COMPONENTS
import UserReportsTable from '../../components/reports/userreports/UserReportsTable';
import UserReportsTableRow from '../../components/reports/userreports/UserReportsTableRow';
import SingleViewUser from '../../components/reports/userreports/SingleViewUser';
import SingleViewSent from '../../components/reports/userreports/SingleViewSent';
import SingleViewStandard from '../../components/reports/userreports/SingleViewStandard';
import SingleViewInternational from '../../components/reports/userreports/SingleViewInternational';
import SingleViewReplies from '../../components/reports/userreports/SingleViewReplies';
import SingleViewDirect from '../../components/reports/userreports/SingleViewDirect';
import SingleViewPending from '../../components/reports/userreports/SingleViewPending';
import SingleViewCall from '../../components/reports/userreports/SingleViewCallForwarding';
import DateRangeSelector from '../../components/common/DateRangeSelector';
import DataExportModal from '../../components/common/modals/DataExportModal';
import Layout from '../../components/layouts/Layout';
import Loader from '../../components/common/Loader';

import { ReportsRoutes } from '../../routes/ReportsRoutes';

// NETWORKING
import useUserReportRequest from '../../requests/UserReportRequest';

// FORMATTED MESSAGES
import UserReportsFormattedMessages from '../../messages/reports/UserReportsFormattedMessages';

const UserReports = (props) => {
  const [smsList, setSmsList] = useState(null);
  const [data, setData] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [dataExists, setDataExists] = useState(false);
  const [totals, setTotals] = useState({
    Direct: 0,
    Standard: 0,
    Replies: 0,
    Sent: 0,
    Pending: 0,
    International: 0,
    Call: 0,
  });
  const [selectedData, setSelectedData] = useState(null);
  const [activePage, setActivePage] = useState('list');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [timeRange, setTimeRange] = useState(null);
  const [displayLetters, setDisplayLetters] = useState([]);
  const [filterLetter, setFilterLetter] = useState('');
  const [loading, setLoading] = useState(true);
  const [costCentreId, setCostCentreId] = useState(null);
  const [filterUserId, setFilterUserId] = useState(null);
  const [filterUserName, setFilterUserName] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showEmailSentModal, setShowEmailSentModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [pageTitle, setPageTitle] = useState(
    UserReportsFormattedMessages.topUserReportsTitle
  );
  const { getReportData, exportUserReportData } = useUserReportRequest();

  useEffect(() => {
    if (props.customer && props.customer !== null) {
      fetchData();
    }
  }, [props.customer]);

  const fetchData = async () => {
    let { userData } = props.location?.state || props;
    let costCentreId = props.location?.state?.selected;
    let userId = undefined;
    if (userData) userId = userData.customer_user_id;

    setCostCentreId(costCentreId);
    setFilterUserId(userId);
    setFilterUserName(userData ? userData.user_name : null);
  };

  const formatData = (data) => {
    try {
      let { accessLevel, accessType } = props;
      let total = {
        Direct: 0,
        Standard: 0,
        Replies: 0,
        Sent: 0,
        International: 0,
        Pending: 0,
        Call: 0,
        DirectParts: 0,
        StandardParts: 0,
        InternationalParts: 0,
        RepliesParts: 0,
        SentParts: 0,
        PendingParts: 0,
        CallMinutes: 0,
      };

      let letterMap = {};
      let letters = [];
      // Setup HTML table data
      let CleanedData = data
        .filter((el) => {
          if (
            displayLetters?.length === 0 &&
            !letterMap[el.user_email.toUpperCase().charAt(0)]
          ) {
            letterMap[el.user_email.toUpperCase().charAt(0)] = true;
            letters.push(`${el.user_email.toUpperCase().charAt(0)}`);
          }

          if (
            filterLetter === '' ||
            filterLetter.toLowerCase() === el.user_email.charAt(0).toLowerCase()
          ) {
            return true;
          }
        })
        .map((el, index) => {
          // Check if first letter of name is in array

          return (
            <UserReportsTableRow
              key={`key_${index}`}
              id={index}
              name={el.user_email}
              active={el.user_status === 'A' ? 'Active' : 'Disabled'}
              Direct={{
                sent: el.direct_sent,
                parts: el.direct_parts,
                onClick: () => changePage('svDir', el),
              }}
              Standard={{
                sent: el.messages_sent,
                parts: el.messages_parts,
                onClick: () => changePage('svStand', el),
              }}
              International={{
                sent: el.premium_sent,
                parts: el.premium_parts,
                onClick: () => changePage('svInt', el),
              }}
              Replies={{
                sent: el.replies_sent,
                parts: el.replies_parts,
                onClick: () => changePage('svRep', el),
              }}
              Sent={{
                sent: el.reminders_sent,
                parts: el.reminders_parts,
                onClick: () => changePage('svSent', el),
              }}
              Pending={{
                sent: el.reminders_pending,
                parts: el.reminders_pending_parts,
                onClick: () => changePage('svPend', el),
              }}
              Call={{
                sent: el.forwarded_calls,
                parts: el.forwarded_calls_parts,
                onClick: () => changePage('svCall', el),
              }}
              isGodAdmin={accessLevel === '3' && accessType === '3'}
              onClickName={() =>
                props.navigate('/account/users', {
                  state: { stv: true, selected: el.customer_user_id },
                })
              }
              onClickStatus={() =>
                props.navigate('/account/users', {
                  state: { stv: true, selected: el.customer_user_id },
                })
              }
            />
          );
        });
      // Calculate totals for bottom of table
      data.forEach((el) => {
        if (
          filterLetter === '' ||
          filterLetter.toLowerCase() === el.user_email.charAt(0).toLowerCase()
        ) {
          total.Direct = total.Direct + parseInt(el.direct_sent);
          total.Standard = total.Standard + parseInt(el.messages_sent);
          total.International = total.International + parseInt(el.premium_sent);
          total.Replies = total.Replies + parseInt(el.replies_sent);
          total.Sent = total.Sent + parseInt(el.reminders_sent);
          total.Pending = total.Pending + parseInt(el.reminders_pending);
          total.Call = total.Call + parseInt(el.forwarded_calls);
          total.DirectParts = total.DirectParts + parseInt(el.direct_parts);
          total.StandardParts =
            total.StandardParts + parseInt(el.messages_parts);
          total.InternationalParts =
            total.InternationalParts + parseInt(el.premium_parts);
          total.RepliesParts = total.RepliesParts + parseInt(el.replies_parts);
          total.SentParts = total.SentParts + parseInt(el.reminders_parts);
          total.PendingParts =
            total.PendingParts + parseInt(el.reminders_pending_parts);
          total.CallMinutes =
            total.CallMinutes + parseInt(el.forwarded_calls_parts);
        }
      });

      if (letters.length > 0) {
        letters.sort();
        setDisplayLetters(letters);
      }
      setSmsList(CleanedData);
      setTotals(total);
      setLoading(false);
    } catch (e) {
      console.error('data error:', e);
    }
  };

  const changePage = (page, value) => {
    // Update activePage
    setActivePage(page);
    setSelectedData(value);
  };

  const backButton = async () => {
    setPageTitle(UserReportsFormattedMessages.topUserReportsTitle);
    setActivePage('list');
  };

  useEffect(() => {
    formatData(data);
  }, [filterLetter]);

  const handleLetterFilter = async (letter) => {
    setFilterLetter((state) => (state !== letter ? letter : ''));
  };

  /*
    Gets the object of the selected User from the data list
  */
  /*getSelectedUser = async (userId) => {
    try {
      let users = data;
      for (let i = 0; i < users.length; i++) {
        if (userId === users[i].userId) {
          return users[i].user;
        }
      }
    }
    catch(e) {
      // TODO - Handle Error
    }
  }*/

  /*
    Handles searching User Data between two dates
  */
  const handleUserDataSearch = useCallback(async () => {
    setDataExists(false);
    setLoading(true);
    setData([]);
    setActiveUsers([]);
    setAllUsers([]);
    const searchParams = {
      fromDate: fromDate,
      toDate: toDate,
      timeRange: timeRange,
      timeRangeStart: fromDate,
      timeRangeEnd: toDate,
      costCentreId: costCentreId,
      filterLetter: null,
      userId: filterUserId,
    };

    await getReportData(searchParams, props.errorHandler).then(({ data }) => {
      console.log(data);
      let displayedList = [];
      let activeUsers = data.filter((e) => {
        return e.user_status === 'A';
      });
      if (showAll) {
        displayedList = data;
      } else {
        displayedList = activeUsers;
      }

      setDataExists(true);
      setData(displayedList);
      formatData(displayedList);
      setActiveUsers(activeUsers);
      setAllUsers(data);
    });
  }, [fromDate, toDate]);

  useEffect(() => {
    if (fromDate && toDate) handleUserDataSearch();
  }, [handleUserDataSearch]);

  const exportUserReport = async () => {
    setExporting(true);
    const searchParams = {
      fromDate: fromDate,
      toDate: toDate,
      costCentreId: costCentreId,
      filterLetter: null,
      userId: filterUserId,
    };
    const response = await exportUserReportData(
      searchParams,
      props.errorHandler
    );
    console.log('response:', response);
    setShowEmailSentModal(true);
    setEmailRecipient(response);
    setExporting(false);
  };

  const generateLander = () => {
    // Display cost centre name if filtering by cost centre
    if (props.location?.state?.selected)
      return (
        <>
          <Typography variant="h2" gutterBottom>
            {UserReportsFormattedMessages.topUserReportsTitle}
          </Typography>
          <Typography gutterBottom>
            {UserReportsFormattedMessages.filterTitle}{' '}
            {props.location.state.selectedName}{' '}
          </Typography>
        </>
      );

    // Display user name if filtering by cost centre
    if (filterUserId)
      return (
        <>
          <Typography variant="h2" gutterBottom>
            {UserReportsFormattedMessages.topUserReportsTitle}
          </Typography>
          <Typography gutterBottom>
            {UserReportsFormattedMessages.filterUserTitle} {filterUserName}{' '}
          </Typography>
        </>
      );

    return null;
  };

  const generateSearchTools = () => {
    return (
      <Box sx={{ marginBottom: { xs: 4, md: 2 } }}>
        <DateRangeSelector
          handleDateSearch={(fromDate, toDate, timeRange) =>
            handleUserDataSearch(fromDate, toDate, timeRange)
          }
          parentStartDate={fromDate}
          setParentStartDate={setFromDate}
          parentEndDate={toDate}
          setParentEndDate={setToDate}
        />
      </Box>
    );
  };

  const handleInputChange = (event) => {
    const target = event.target;
    const name = target.name;
    const value = target.type === 'checkbox' ? target.checked : target.value;

    console.log('handleInputChange event name:', name);
    console.log('handleInputChange event value:', value);

    if (name === 'showAll') {
      setShowAll(value);
      if (value === false) {
        setData(activeUsers);
        formatData(activeUsers);
      } else {
        setData(allUsers);
        formatData(allUsers);
      }
    }
  };

  const generateFirstLetterFilter = () => {
    if (props.userData) return null;
    if (displayLetters !== undefined && displayLetters !== 'undefined') {
      const letterHtml = (displayLetters || []).map((letter) => {
        return (
          <Button
            key={letter}
            variant={'text'}
            onClick={() => handleLetterFilter(letter)}
            size="small"
            sx={{
              minWidth: 'auto',
              padding: 0.25,
              color:
                filterLetter === letter ? 'primary.main' : 'secondary.light',
            }}
          >
            {letter}
          </Button>
        );
      });

      return (
        <Stack
          sx={{
            flexDirection: { xs: 'column-reverse', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { md: 'end' },
            width: '100%',
          }}
        >
          <Stack direction="row" sx={{ marginBottom: 0.25 }}>
            {letterHtml}
          </Stack>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              marginBottom: 2,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  name="showAll"
                  checked={showAll}
                  onChange={(e) => handleInputChange(e)}
                  sx={{ padding: 0.45 }}
                />
              }
              label={'Show All'}
              sx={{ margin: 0 }}
            />
            <Button
              variant="contained"
              color="secondary"
              onClick={exportUserReport}
              disabled={exporting}
            >
              Email Report
            </Button>
          </Box>
        </Stack>
      );
    }
    return null;
  };

  const renderContent = () => {
    let { accessLevel, accessType } = props;
    if (accessLevel <= '0' || accessType === '0') {
      return null;
    }
    switch (activePage) {
      case 'svPend':
        return (
          <SingleViewPending
            user={selectedData}
            setPageTitle={setPageTitle}
            fromDate={fromDate}
            toDate={toDate}
            errorHandler={props.errorHandler}
          />
        );
      case 'svDir':
        return (
          <SingleViewDirect
            user={selectedData}
            setPageTitle={setPageTitle}
            fromDate={fromDate}
            toDate={toDate}
            errorHandler={props.errorHandler}
          />
        );
      case 'svRep':
        return (
          <SingleViewReplies
            user={selectedData}
            setPageTitle={setPageTitle}
            fromDate={fromDate}
            toDate={toDate}
            errorHandler={props.errorHandler}
          />
        );
      case 'svSent':
        return (
          <SingleViewSent
            user={selectedData}
            setPageTitle={setPageTitle}
            fromDate={fromDate}
            toDate={toDate}
            errorHandler={props.errorHandler}
          />
        );
      case 'svStand':
        return (
          <SingleViewStandard
            user={selectedData}
            setPageTitle={setPageTitle}
            fromDate={fromDate}
            toDate={toDate}
            errorHandler={props.errorHandler}
          />
        );
      case 'svInt':
        return (
          <SingleViewInternational
            user={selectedData}
            setPageTitle={setPageTitle}
            fromDate={fromDate}
            toDate={toDate}
            errorHandler={props.errorHandler}
          />
        );
      case 'svCall':
        return (
          <SingleViewCall
            user={selectedData}
            setPageTitle={setPageTitle}
            fromDate={fromDate}
            toDate={toDate}
            errorHandler={props.errorHandler}
          />
        );
      case 'svUser':
        return (
          <SingleViewUser
            selectedData={selectedData}
            setPageTitle={setPageTitle}
            errorHandler={props.errorHandler}
          />
        );
      case 'svDetail':
        return <Loader />;
      default:
        return (
          <>
            {generateLander()}
            {generateSearchTools()}

            {loading ? (
              <Loader />
            ) : (
              <UserReportsTable
                changePage={changePage}
                totals={totals}
                list={smsList}
                displayLetters={displayLetters}
                filter={generateFirstLetterFilter}
              />
            )}
            <DataExportModal
              show={showEmailSentModal}
              onHide={() => setShowEmailSentModal(false)}
              emailRecipient={emailRecipient}
            />
          </>
        );
    }
  };

  return (
    <Layout
      routes={ReportsRoutes}
      title={pageTitle}
      accessLevel={props.accessLevel}
      accessType={props.accessType}
      backButtonText={
        activePage !== 'list' ? UserReportsFormattedMessages.backText : null
      }
      handleBackButton={activePage !== 'list' ? backButton : null}
    >
      {renderContent()}
    </Layout>
  );
};

export default UserReports;
